import React, { useEffect, useState } from 'react';
import { formatCompactCurrency } from '../utils/formatters';
import api from '../services/api';

interface AcquisitionsData {
  config: {
    companyName: string;
    currentEBITDA: number;
    fy26Target: number;
  };
  summary: {
    totalValue: number;
    weightedValue: number;
    count: number;
  };
  acquisitions: Array<{
    id: string;
    name: string;
    value: number;
    status: string;
    probability: number;
    sector?: string;
    targetDate?: string;
    notes?: string;
  }>;
}

export const AcquisitionsDashboard: React.FC = () => {
  const [data, setData] = useState<AcquisitionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/acquisitions');
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load acquisitions data');
        }
      } catch (err) {
        setError('Failed to load acquisitions data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  const getConfidenceBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('high')) return 'bg-success text-white';
    if (statusLower.includes('medium')) return 'bg-warning text-black';
    if (statusLower.includes('low')) return 'bg-danger text-white';
    return 'bg-primary text-white';
  };

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-primary mb-2">
              Strategic Acquisitions Dashboard
            </h1>
            <p className="text-lg text-text-secondary">
              Strategic M&A Opportunities - Path to EBITDA Target
            </p>
            <p className="text-sm text-text-muted mt-2">
              Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Acquisition Pipeline Overview */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Acquisition Pipeline Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-primary uppercase mb-2 font-bold">Total Pipeline Value</div>
              <div className="text-5xl font-bold text-primary my-3">
                {formatCompactCurrency(data.summary.totalValue)}
              </div>
              <div className="text-xs text-text-muted uppercase">Strategic Acquisition Targets</div>
            </div>
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-primary uppercase mb-2 font-bold">Active Opportunities</div>
              <div className="text-5xl font-bold text-primary my-3">
                {data.summary.count}
              </div>
              <div className="text-xs text-text-muted uppercase">Companies Under Review</div>
            </div>
          </div>
        </section>

        {/* Strategic Acquisition Targets */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Strategic Acquisition Targets
          </h2>
          <div className="space-y-6">
            {data.acquisitions.map((acquisition, index) => (
              <div
                key={acquisition.id}
                className="border-2 border-primary rounded-lg p-6"
              >
                {/* Acquisition Title */}
                <div className="text-primary text-2xl font-bold mb-5">
                  {index + 1}. {acquisition.name} - {formatCompactCurrency(acquisition.value * (acquisition.probability / 100))}
                </div>

                {/* Acquisition Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-5 text-center">
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Value</div>
                    <div className="text-primary text-xl font-bold">
                      {formatCompactCurrency(acquisition.value)}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Target Timeline</div>
                    <div className="text-text-primary text-base">
                      {acquisition.targetDate || 'FY27'}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Confidence</div>
                    <div>
                      <span
                        className={`inline-block px-4 py-1.5 rounded-full font-bold text-xs ${getConfidenceBadgeClass(
                          acquisition.status
                        )}`}
                      >
                        {acquisition.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Category</div>
                    <div className="text-text-primary text-sm">
                      {acquisition.sector || 'Strategic Acquisitions'}
                    </div>
                  </div>
                </div>

                {/* Strategic Notes & Progress Updates */}
                <div className="bg-background rounded p-5">
                  <div className="text-primary font-bold mb-3">Strategic Notes & Progress Updates:</div>
                  <div className="bg-surface-light border border-border rounded p-4 min-h-[80px]">
                    <p className="text-text-secondary text-sm">
                      {acquisition.notes || '[Add acquisition progress, due diligence status, and strategic fit notes here]'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Acquisition Pipeline Summary Table */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Acquisition Pipeline Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-light border-b-2 border-primary">
                  <th className="text-left py-3 px-4 text-primary font-bold">Target Company</th>
                  <th className="text-right py-3 px-4 text-primary font-bold">Value</th>
                  <th className="text-center py-3 px-4 text-primary font-bold">Timeline</th>
                  <th className="text-center py-3 px-4 text-primary font-bold">Confidence</th>
                  <th className="text-left py-3 px-4 text-primary font-bold">Strategic Rationale</th>
                </tr>
              </thead>
              <tbody>
                {data.acquisitions.map((acquisition) => (
                  <tr
                    key={acquisition.id}
                    className="border-b border-border hover:bg-surface-light transition-colors"
                  >
                    <td className="py-4 px-4">
                      <p className="font-medium text-text-primary">{acquisition.name}</p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="font-bold text-primary">
                        {formatCompactCurrency(acquisition.value)}
                      </p>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <p className="text-text-secondary">{acquisition.targetDate || 'FY27'}</p>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getConfidenceBadgeClass(
                          acquisition.status
                        )}`}
                      >
                        {acquisition.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-text-secondary">
                        {acquisition.sector || 'Strategic acquisition opportunity'}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};
